if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const currentPath = this.dataset.url ? this.dataset.url.split('?')[0].split('#')[0] : '';
        const targetPath = target.dataset.productUrl ? target.dataset.productUrl.split('?')[0].split('#')[0] : '';
        const shouldSwapProduct = Boolean(targetPath && currentPath && currentPath !== targetPath);
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Instant synchronous update from selected options if available
        this.syncVariantFromSelection();

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      getProductVariants() {
        const script = this.querySelector('variant-selects [data-product-variants]');
        if (script && script.textContent) {
          try {
            return JSON.parse(script.textContent);
          } catch (e) {
            console.warn('Failed to parse data-product-variants', e);
          }
        }
        return null;
      }

      getSelectedOptionValues() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return [];
        const values = [];
        const optionContainers = variantSelects.querySelectorAll('fieldset.product-form__input, .product-form__input--dropdown');
        if (optionContainers.length > 0) {
          optionContainers.forEach((container) => {
            const checkedRadio = container.querySelector('input[type="radio"]:checked');
            if (checkedRadio) {
              values.push(checkedRadio.value.trim());
              return;
            }
            const select = container.querySelector('select');
            if (select && select.value) {
              values.push(select.value.trim());
            }
          });
        } else {
          variantSelects.querySelectorAll('input[type="radio"]:checked, select').forEach((el) => {
            if (el.value) values.push(el.value.trim());
          });
        }
        return values;
      }

      findVariantByOptions(options) {
        const variants = this.getProductVariants();
        if (!Array.isArray(variants) || !options.length) return null;
        return variants.find((v) => {
          return options.every((optVal, idx) => {
            const key = `option${idx + 1}`;
            return !v[key] || v[key].trim().toLowerCase() === optVal.trim().toLowerCase();
          });
        }) || null;
      }

      findCodBlocks() {
        const blocks = new Set(this.querySelectorAll('.cod-buy-now-popup-block'));
        const sectionContainer =
          this.closest('.shopify-section, .section') ||
          (this.dataset.section ? document.getElementById(`shopify-section-${this.dataset.section}`) : null) ||
          this.parentElement;
        if (sectionContainer) {
          sectionContainer.querySelectorAll('.cod-buy-now-popup-block').forEach((b) => blocks.add(b));
        }
        return Array.from(blocks);
      }

      syncCodAppBlock(variant) {
        if (!variant) return;
        const codBlocks = this.findCodBlocks();
        const variantId = String(variant.id);
        const variantGid = `gid://shopify/ProductVariant/${variantId}`;
        const priceCents = Math.round(Number(variant.price || 0));
        const variantTitle = variant.title || '';
        const productImage = variant.featured_image?.src || variant.featured_media?.preview_image?.src || '';
        const productGid = this.dataset.productId ? `gid://shopify/Product/${this.dataset.productId}` : '';

        codBlocks.forEach((block) => {
          block.dataset.variantGid = variantGid;
          block.dataset.priceCents = priceCents;

          const variantField = block.querySelector('[data-buy-now-variant-field]');
          if (variantField) {
            variantField.value = variantId;
            variantField.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const payOnlineField = block.querySelector('[data-buy-now-pay-online-variant-field]');
          if (payOnlineField) {
            payOnlineField.value = variantId;
          }

          const confirmButton = block.querySelector('[data-buy-now-confirm], .cod-bn-confirm-btn');
          if (confirmButton) {
            confirmButton.dataset.variantGid = variantGid;
          }

          block.dispatchEvent(
            new CustomEvent('cst:apply-external-product', {
              bubbles: true,
              detail: {
                productGid: productGid || block.dataset.productGid || '',
                variantId: variantId,
                productTitle: block.dataset.productTitle || '',
                variantTitle: variantTitle,
                productImage: productImage,
                priceCents: priceCents,
              },
            })
          );
        });

        // Also update any custom buy now buttons in the section
        const customButtons = new Set(this.querySelectorAll('[data-cst-buy-now-button]'));
        const sectionContainer =
          this.closest('.shopify-section, .section') ||
          (this.dataset.section ? document.getElementById(`shopify-section-${this.dataset.section}`) : null) ||
          this.parentElement;
        if (sectionContainer) {
          sectionContainer.querySelectorAll('[data-cst-buy-now-button]').forEach((btn) => customButtons.add(btn));
        }
        customButtons.forEach((btn) => {
          btn.setAttribute('data-variant-id', variantId);
          btn.setAttribute('data-variant-title', variantTitle);
          btn.setAttribute('data-price-cents', priceCents);
          btn.setAttribute('data-product-price-cents', priceCents);
          if (productImage) btn.setAttribute('data-product-image', productImage);
        });
      }

      syncVariantFromSelection() {
        const options = this.getSelectedOptionValues();
        const variant = this.findVariantByOptions(options);
        if (variant) {
          this.updateVariantInputs(variant.id, variant.available);
          this.syncCodAppBlock(variant);
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const [baseUrl, search] = (url || this.dataset.url || '').split('?');
        const searchParams = new URLSearchParams(search || '');

        if (!shouldFetchFullPage) {
          searchParams.set('section_id', this.sectionId);
        }

        if (optionValues && optionValues.length) {
          searchParams.set('option_values', optionValues.join(','));
        }

        const queryString = searchParams.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id, variant?.available);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.syncCodAppBlock(variant);
          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId, available = true) {
        const formSelectors = `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, form[data-type="add-to-cart-form"]`;
        this.querySelectorAll(formSelectors).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"], select[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            if (available !== undefined) {
              input.disabled = !variantId || !available;
            }
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });

        this.querySelectorAll('input[name="id"], select[name="id"], .product-variant-id, [data-buy-now-variant-field]').forEach((input) => {
          if (input.value !== (variantId ? String(variantId) : '')) {
            input.value = variantId ?? '';
            if (available !== undefined && !input.hasAttribute('data-buy-now-variant-field')) {
              input.disabled = !variantId || !available;
            }
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });

        if (variantId) {
          const variantChangeEvent = new CustomEvent('variant:change', {
            bubbles: true,
            composed: true,
            detail: {
              variant: variantId,
              variantId: variantId,
              sectionId: this.sectionId,
            },
          });
          this.dispatchEvent(variantChangeEvent);
          document.dispatchEvent(variantChangeEvent);
        }
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
        this.updateVariantInputs(null, false);
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );

  // Global click interception in capture phase to guarantee variant resolution
  // before COD app popup handlers execute on either [data-cst-buy-now-button] or [data-buy-now-trigger]
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-cst-buy-now-button], .custom-buy-now-btn, [data-buy-now-trigger]');
      if (!trigger) return;

      const productInfo =
        trigger.closest('product-info') ||
        (trigger.closest('[data-section]')
          ? document.querySelector(`product-info[data-section="${trigger.closest('[data-section]').dataset.section}"]`)
          : null) ||
        trigger.closest('.shopify-section, .section')?.querySelector('product-info') ||
        document.querySelector('product-info');

      if (!productInfo) return;

      if (typeof productInfo.syncVariantFromSelection === 'function') {
        productInfo.syncVariantFromSelection();
      }

      const currentInput = productInfo.querySelector('input[name="id"], select[name="id"], .product-variant-id');
      const resolvedVariantId = currentInput?.value;
      if (resolvedVariantId) {
        if (trigger.matches('[data-cst-buy-now-button]')) {
          trigger.setAttribute('data-variant-id', resolvedVariantId);
        }
        const codBlock = trigger.closest('.cod-buy-now-popup-block') || productInfo.querySelector('.cod-buy-now-popup-block');
        if (codBlock) {
          codBlock.dataset.variantGid = `gid://shopify/ProductVariant/${resolvedVariantId}`;
          const variantField = codBlock.querySelector('[data-buy-now-variant-field]');
          if (variantField) variantField.value = resolvedVariantId;
          const payOnlineField = codBlock.querySelector('[data-buy-now-pay-online-variant-field]');
          if (payOnlineField) payOnlineField.value = resolvedVariantId;
          const confirmBtn = codBlock.querySelector('[data-buy-now-confirm], .cod-bn-confirm-btn');
          if (confirmBtn) confirmBtn.dataset.variantGid = `gid://shopify/ProductVariant/${resolvedVariantId}`;
        }
      }
    },
    true
  );
}
